import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import MetaTags from '@/components/Head/MetaTags';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import { H3, H4 } from '@/components/Element/Heading';
import SmartLink from '@/components/Button/SmartLink';

import LogoFullNameBlueImage from 'public/brand/EnergyPal-Logo-FullName-blue.png';
import LogoFullNameWhiteImage from 'public/brand/EnergyPal-Logo-FullName-white.png';

import LogoEBlueImage from 'public/brand/EnergyPal-Logo-E-blue.png';
import LogoEWhiteImage from 'public/brand/EnergyPal-Logo-E-white.png';
import Image from 'next/legacy/image';

import Imagery1 from 'images/brand-assets/image1.jpg';
import Imagery2 from 'images/brand-assets/image2.png';
import Imagery3 from 'images/brand-assets/image3.png';
import Imagery4 from 'images/brand-assets/image4.jpg';
import Imagery5 from 'images/brand-assets/image5.jpg';
import Imagery6 from 'images/brand-assets/image7.png';

export default function Page() {
  const images = [Imagery6, Imagery2, Imagery1, Imagery3, Imagery4, Imagery5];
  return (
    <article>
      <MetaTags
        title="Company Brand Assets - EnergyPal"
        description="Our Brand EnergyPal helps homeowners go solar for less. Find the best deals on top-rated solar & home battery systems, compare costs and install with ease."
      />
      <div className="relative bg-gray-800">
        <div className="relative px-4 py-16 mx-auto max-w-7xl sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base font-semibold tracking-wide uppercase text-accent-400">
              Company
            </p>

            <h1
              className="text-4xl font-bold text-white
                sm:text-5xl sm:tracking-tight lg:text-5xl"
            >
              Brand Assets
            </h1>

            <h2 className="mt-5 text-xl text-gray-100">
              {' '}
              If you are looking to partner with EnergyPal or have a media
              inquiry, please{' '}
              <span className="underline">
                <strong>
                  <SmartLink href="/contact-us-customer-care">
                    connect with us here
                  </SmartLink>
                </strong>
              </span>
              .
            </h2>
          </div>
        </div>
      </div>

      <FullWidthBarBreadcrumbs
        pages={[{ name: 'Brand Assets', href: '/company-brand-assets' }]}
      />
      <CenteredContentSection>
        <H3>Describing EnergyPal</H3>
        <p>
          If you are featuring EnergyPal, use consistent language throughout all
          communications. EnergyPal provides homeowners with better energy
          products, better value and pricing, and overall a better shopping
          experience.
        </p>
        <H4>Brand Motto</H4>
        <p>EnergyPal: Solar &amp; Home Batteries for Less!</p>
        <H4>Brand Slogan</H4>
        <p>Save more, get more with EnergyPal.</p>
        <H4>Short Description</H4>
        <p>
          EnergyPal helps you go solar, for less. Find the best deals on
          top-rated solar & home battery systems, compare costs and shop
          stress-free.
        </p>
        <H4>Boilerplate</H4>
        <p>
          EnergyPal is a multinational provider of high quality energy
          solutions. We give homeowners the best value, guidance and support in
          choosing solar power and battery storage. Our platform offers
          industry-leading energy products and services and delivers financial
          savings through customer choice, wholesale pricing, proprietary
          technology and digital operations. Please visit EnergyPal.com or call
          +1 (800) 990-3725 to learn more.
        </p>
        <H4>Verbal Pointers</H4>
        <ul>
          <li>
            EnergyPal’s Advisors will get you the best solar energy package for
            your home.
          </li>
          <li>
            EnergyPal is an unbiased service to assist you in shopping for the
            best-fit energy solution for your home.
          </li>
        </ul>
        <H4>Tone and Voice</H4>
        <p>
          Reflect our brand personality through a consistent tone and voice.
          Maintain a balance between the following:
        </p>
        <ul>
          <li>
            <strong>Authoritative:</strong> EnergyPal is your trusted source for
            expert insights in the solar energy industry. Our tone is
            professional and authoritative, backed by decades of experience and
            a deep understanding of solar power, home energy use, utility rates,
            EV charging, and battery storage.
          </li>
          <li>
            <strong>Approachable:</strong> At EnergyPal, we&apos;re your
            friendly Energy Advisors on your switch to solar journey. Our tone
            is approachable, like a conversation with a knowledgeable,
            experienced friend who&apos;s always ready to share insights and
            help you figure out the best solution.
          </li>
          <li>
            <strong>Reliable:</strong> When you choose EnergyPal, you&apos;re
            choosing customer trust and reliability. Our tone is steadfast,
            delivering accurate solar and energy solutions information you can
            depend on. You can also depend on EnergyPal to advocate for you the
            customer to ensure your project is done right.
          </li>
          <li>
            <strong>Honest:</strong> EnergyPal believes honesty is the best
            policy and our path to being the #1 solar energy marketplace and
            shopping platform. Our tone is down-to-earth and honest, giving you
            straightforward information, and side-by-side product comparisons,
            without any hidden agendas.
          </li>
        </ul>
        <H3>Our Logo</H3>
        <p>
          The EnergyPal logo can be used in color or white. Please don&apos;t
          alter the EnergyPal brand logo in any way. Use approved logo
          variations provided in the brand assets package. Do not alter the
          logo&apos;s proportions, colors, or elements. Maintain adequate clear
          space around the logo. This area should be free from text, images, or
          other elements to ensure visibility and impact.
        </p>
        <H4>Full Logo</H4>
        <p>
          Our logo includes our name written as a mark. When displayed on white
          or bold blue it is shown in two-tone. When on gray or black, it is
          shown as a single tone. Preserve the logo&apos;s legibility by
          avoiding resizing below the specified dimensions. For smaller
          applications, use alternative logo versions as provided.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <div>
            <Image
              src={LogoFullNameBlueImage}
              alt="EnergyPal Full Logo - Blue"
            />
            <div className="space-x-3 text-sm text-right">
              <a href="/brand/EnergyPal-Logo-FullName-blue.png" target="_blank">
                png
              </a>
              <a href="/brand/EnergyPal-Logo-FullName-blue.svg" target="_blank">
                svg
              </a>
            </div>
          </div>
          <div>
            <div className="bg-primary">
              <Image
                src={LogoFullNameWhiteImage}
                alt="EnergyPal Full Logo - White"
              />
            </div>
            <div className="space-x-3 text-sm text-right">
              <a
                href="/brand/EnergyPal-Logo-FullName-white.png"
                target="_blank"
              >
                png
              </a>
              <a
                href="/brand/EnergyPal-Logo-FullName-white.svg"
                target="_blank"
              >
                svg
              </a>
            </div>
          </div>
        </div>
        <H4>Logo Mark</H4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <div>
            <Image src={LogoEBlueImage} alt="EnergyPal Letter Logo - Blue" />
            <div className="space-x-3 text-sm text-right">
              <a href="/brand/EnergyPal-Logo-E-blue.png" target="_blank">
                png
              </a>
              <a href="/brand/EnergyPal-Logo-E-blue.svg" target="_blank">
                svg
              </a>
            </div>
          </div>
          <div>
            <div className="bg-primary">
              <Image
                src={LogoEWhiteImage}
                alt="EnergyPal Letter Logo - White"
              />
            </div>
            <div className="space-x-3 text-sm text-right">
              <a href="/brand/EnergyPal-Logo-E-white.png" target="_blank">
                png
              </a>
              <a href="/brand/EnergyPal-Logo-E-white.svg" target="_blank">
                svg
              </a>
            </div>
          </div>
        </div>
        <H4>Our Colors</H4>
        <p>
          Our primary color palette is composed of a bold blue, a medium gray,
          and white. We use a light powder blue and various shades of grey as
          secondary colors.
        </p>
        <div className="grid grid-cols-1 text-sm text-center sm:grid-cols-2 md:grid-cols-4 gap-x-4">
          <div>
            <div className="mb-3 border rounded-full bg-primary aspect-square" />
            <strong>EnergyPal Bold Blue</strong>
            <div>#1155CC</div>
            <div>rgb(17, 85, 204)</div>
          </div>
          <div>
            <div className="mb-3 border rounded-full bg-accent aspect-square" />
            <strong>EnergyPal Powder Blue</strong>
            <div>#C9DAF8</div>
            <div>rgb(201, 218, 248)</div>
          </div>
          <div>
            <div className="mb-3 border rounded-full bg-secondary aspect-square" />
            <strong>Medium Gray</strong>
            <div>#888888</div>
            <div>rgb(136, 136, 136)</div>
          </div>
          <div>
            <div className="mb-3 bg-white border rounded-full aspect-square" />
            <strong>White</strong>
            <div>#FFFFFF</div>
            <div>rgb(255, 255, 255)</div>
          </div>
        </div>
        <H3>Imagery</H3>
        <p>
          <strong>Photography Style:</strong> Use photography that aligns with
          our brand&apos;s tone and values, portraying a consistent visual
          narrative. This means up to date and modern home solar panel and
          battery products are shown in imagery.{' '}
        </p>
        <div className="grid grid-cols-2 gap-4">
          {images.map((src, index) => (
            <div key={index} className="relative h-60 w-full">
              <Image
                src={src}
                alt={`Imagery ${index + 1}`}
                layout="fill"
                objectFit="cover"
              />
            </div>
          ))}
        </div>
        <p>
          <strong>Iconography:</strong> When using icons, maintain a consistent
          style that resonates with our brand identity. Stick to our brand
          colors, while keeping a professional and modern look.
        </p>
        <H4>Typography</H4>
        <p>
          <strong>Font Selection:</strong> Headings are Montserrat. Roboto is
          the official brand typeface for both print and digital materials.
          Maintain consistent font usage for body text to uphold the
          brand&apos;s character.{' '}
        </p>
        {/* <hr />
        <p>
          If you are looking to partner with EnergyPal or have a media inquiry,
          please{' '}
          <SmartLink href="/contact-us-customer-care">
            connect with us here
          </SmartLink>
          .
        </p> */}
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
